import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback } from 'react'
import { DndContext, closestCenter, KeyboardSensor, useSensor, useSensors, DragStartEvent, DragOverlay, DragMoveEvent, DragEndEvent, DragOverEvent, MeasuringStrategy, DropAnimation, defaultDropAnimation, MouseSensor} from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { buildTree, flattenTree, getProjection, getChildCount, removeChildrenOf, setProperty } from './utilities'
import { FlattenedItem, SensorContext, TreeItem, TreeItems } from './types'
import { sortableTreeKeyboardCoordinates } from './keyboard-coordinates'
import { CSS } from '@dnd-kit/utilities'
import { IHeader, IInfoGoHeaderOrg, IParentChangeInput, IPresentation, IPresentationInput } from '../../../../types/infogo'
import HeaderOrgItem from './header-org-item'
import { useLazyQuery, useMutation } from '@apollo/client'
import { DELETE_HEADER, GET_HEADER, GET_INFOGO_HEADER_ORGS } from '../../../../graphql/infogo-gql'
import _ from 'lodash';
import { IOrg } from '../../../../types/org'
import SpinnerOverlay from '../../../common/spinner/spinner';
import { AddIcon, DeleteIcon } from '../../../common/icons'
import useToggle from '../../../../hook/useToggle'
import HeaderAdd from './header-add'
import HeaderUpdate from './header-update'
import confirm from '../../../common/confirm'
import { notifySuccess } from '../../../../services/notification'
import { warn } from '../../../../services/env'
import { CategoryType } from '../../../../types/constants'
import useAuth from '../../../../hook/useAuth'
import { useFrench } from '../../../french/french-context'
import Button from '../../../common/button'

const measuring = {
    droppable: {
        strategy: MeasuringStrategy.Always,
    }
}

const dropAnimationConfig: DropAnimation = {
    keyframes({ transform }) {
        return [
            { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
            {
                opacity: 0,
                transform: CSS.Transform.toString({
                    ...transform.final,
                    x: transform.final.x + 5,
                    y: transform.final.y + 5,
                }),
            },
        ]
    },
    easing: 'ease-out',
    sideEffects({ active }) {
        active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: defaultDropAnimation.duration,
            easing: defaultDropAnimation.easing,
        })
    },
}

export type HeaderOrgsHandler = {
    getHeaderOrgsConfig: () => IPresentationInput[];
    getParentChanges: () => IParentChangeInput[];
    reset: () => void;
    hasChange: () => boolean;
    hasNewSuppress: () => boolean;
}

type HeaderOrgsProps = {
    org?: IOrg;
    onChange: () => void;
}

const INDENTATION_WIDTH: number = 50

const HeaderOrgs = forwardRef<HeaderOrgsHandler, HeaderOrgsProps>((props: HeaderOrgsProps, ref: ForwardedRef<any>) => {
    const {org, onChange} = props
    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench()
    const [orgTree, setOrgTree] = useState<TreeItems<IInfoGoHeaderOrg>>([])
    const [suppressedIds, setSuppressedIds] = useState<IPresentation[]>([])

    const [prevSuppress, setPrevSuppress] = useState<IPresentation[]>([])
    const [prevSequence, setPrevSequence] = useState<IPresentation[]>([])

    const [parentChange, setParentChange] = useState<Map<string, string>>(new Map<string, string>())

    const [rootId, setRootId] = useState<string | null>(null)
    const [activeId, setActiveId] = useState<string | null>(null)
    const [overId, setOverId] = useState<string | null>(null)
    const [offsetLeft, setOffsetLeft] = useState(0)

    const [header, setHeader] = useState<IHeader | null>(null)
    const [parentHeaderId, setParentHeaderId] = useState<string | null>(null)
    const {isOpen: addHeaderOpen, toggle: toggleAddHeader} = useToggle()
    const {isOpen: editHeaderOpen, toggle: toggleEditHeader} = useToggle()

    const [getHeaderOrgs, { loading, data }] = useLazyQuery(GET_INFOGO_HEADER_ORGS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.infoGoHeaderOrgs) {
                let orgTree = buildOrgTree(_.cloneDeep(data.infoGoHeaderOrgs))
                setOrgTree(orgTree)

                const suppressVls = data.infoGoHeaderOrgs.filter((e: IInfoGoHeaderOrg) => e.suppress)
                    .map((e: IInfoGoHeaderOrg) => {
                        return {typeName: e.typeName, objectId: e.presentationId}
                    })
                setPrevSuppress(suppressVls)
                setSuppressedIds(suppressVls)

                const clonedItems: FlattenedItem<IInfoGoHeaderOrg>[] = flattenTree(orgTree, rootId)
                const sequenceVls = clonedItems.map((e: FlattenedItem<IInfoGoHeaderOrg>) =>  {
                    return {typeName: e.typeName, objectId: e.presentationId, parentId: e.parentId}
                })
                setPrevSequence(sequenceVls)
            }
        }
    })

    const [getHeader, {loading: headerLoading}] = useLazyQuery(GET_HEADER, {
        onCompleted: (data) => {
            if (data?.infoGoHeader) {
                setHeader(data?.infoGoHeader)
                toggleEditHeader()
            }
        }
    })

    const [deleteHeader, {loading: deleteHeaderLoading}] = useMutation(DELETE_HEADER)

    useEffect(() => {
        if (org) {
            setRootId(org.id.toString())
            getHeaderOrgs({ variables: { orgId: org.id } })
        }
    }, [org])

    useImperativeHandle(ref, () => ({
        getHeaderOrgsConfig() {
            const allItems: FlattenedItem<IInfoGoHeaderOrg>[] = flattenTree(orgTree, rootId)
            let input: IPresentationInput[] = allItems.map((item: FlattenedItem<IInfoGoHeaderOrg>, index: number) => {
                return {
                    typeName: item.typeName!,
                    objectId: item.presentationId!,
                    suppress: isItemSuppressed(item),
                    sequence: index + 1
                }
            })
            return input
        },
        getParentChanges() {
            let changes: IParentChangeInput[] = []
            parentChange.forEach((value: string, key: string) => {
                changes.push({id: key, parentId: value})
            })
            return changes
        },
        reset() {
            onReset()
        },
        hasChange() {
            return isDirty()
        },
        hasNewSuppress() {
            return hasNewSuppressChange()
        }
    }), [data, suppressedIds, orgTree])

    const flattenedItems = useMemo(() => {
        const flattenedTree = flattenTree(orgTree, rootId)
        const collapsedItems = flattenedTree.reduce<string[]>(
            (acc, { children, collapsed, id }) =>
                collapsed && children.length ? [...acc, id] : acc,
            []
        )

        return removeChildrenOf(
            flattenedTree,
            activeId ? [activeId, ...collapsedItems] : collapsedItems
        )
    }, [activeId, orgTree])

    const sensorContext: SensorContext<IInfoGoHeaderOrg> = useRef({
        items: flattenedItems,
        offset: offsetLeft,
    })

    const [coordinateGetter] = useState(() =>
        sortableTreeKeyboardCoordinates(sensorContext, rootId, false, INDENTATION_WIDTH)
    )

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 8
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter,
        })
    )

    const sortedIds = useMemo(() => flattenedItems.map(({ id }) => id), [
        flattenedItems,
    ])

    const activeItem = activeId
        ? flattenedItems.find(({ id }) => id === activeId)
        : null

    useEffect(() => {
        sensorContext.current = {
            items: flattenedItems,
            offset: offsetLeft,
        }
    }, [flattenedItems, offsetLeft])

    const onReset = () => {
        let orgTree = buildOrgTree(_.cloneDeep(data.infoGoHeaderOrgs))
        setOrgTree(orgTree)
        setSuppressedIds(prevSuppress)
        setTimeout(() => onChange())
    }

    const isDirty = () => {
        return reOrdered() || hasSuppressChange()
    }

    const buildOrgTree = (orgs: IInfoGoHeaderOrg[]) : TreeItems<IInfoGoHeaderOrg> => {
        const roots : TreeItems<IInfoGoHeaderOrg> = orgs.filter(x => x.parentId === org?.id)
            .sort(comparator)
            .map(r => r = _recursiveBuildTree(orgs, r))
            .map(r => convertToTreeItem(r))

        return roots
    }

    const _recursiveBuildTree = (orgs: IInfoGoHeaderOrg[], parent: IInfoGoHeaderOrg): IInfoGoHeaderOrg => {
        parent.children = orgs.filter(x => x.parentId === parent.id)
            .sort(comparator)
            .map(y => _recursiveBuildTree(orgs, y))
        return parent
    }

    const convertToTreeItem = (item: IInfoGoHeaderOrg): TreeItem<IInfoGoHeaderOrg> => {
        const { id, children, ...rest } = item;
        return {
            id,
            children: (children || []).map(convertToTreeItem),
            ...rest
        };
    }

    const comparator = (a: IInfoGoHeaderOrg, b: IInfoGoHeaderOrg): number => {
        if (a.sequence && b.sequence) {
            if (a.sequence !== b.sequence) {
                return a.sequence - b.sequence;
            }
        } else if (a.sequence) {
            return -1;
        } else if (b.sequence) {
            return 1;
        }
        return a.name.localeCompare(b.name);
    }

    const projected =
        activeId && overId
            ? getProjection(flattenedItems, activeId, overId, rootId, offsetLeft, INDENTATION_WIDTH)
            : null

    const handleDragStart = ({ active: { id: activeId } }: DragStartEvent) => {
        setActiveId(activeId.toString())
        setOverId(activeId.toString())
    }

    const handleDragMove = ({ delta }: DragMoveEvent) => {
        setOffsetLeft(delta.x)
    }

    const handleDragOver = ({ over }: DragOverEvent) => {
        setOverId(over?.id?.toString() ?? null)
    }

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        resetState()

        if (projected && over) {
            const { depth, parentId } = projected
            const clonedItems: FlattenedItem<IInfoGoHeaderOrg>[] = flattenTree(orgTree, rootId)
            const overIndex = clonedItems.findIndex(({ id }) => id === over.id)

            const activeIndex = clonedItems.findIndex(({ id }) => id === active.id)
            const activeTreeItem = clonedItems[activeIndex]

            clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId }

            const sortedItems = arrayMove(clonedItems, activeIndex, overIndex)
            const newOrgTree = buildTree(sortedItems, rootId)

            setOrgTree(newOrgTree)
            setParentChange((parentMap: Map<string, string>) => {
                if (activeTreeItem.parentId !== parentId) {
                    parentMap.set(activeTreeItem.id, parentId!)
                }
                return parentMap
            })
            setTimeout(()=>onChange())
        }
    }

    const handleDragCancel = () => {
        resetState()
    }

    const reOrdered = (): boolean => {
        const clonedItems: FlattenedItem<IInfoGoHeaderOrg>[] = flattenTree(orgTree, rootId)
        const newSequence = clonedItems.map((e: FlattenedItem<IInfoGoHeaderOrg>) =>  {
            return {typeName: e.typeName, objectId: e.presentationId, parentId: e.parentId}
        })
        if (prevSequence.length > 0) {
            return prevSequence.some((item, index) => item.typeName !== newSequence[index].typeName || item.objectId !== newSequence[index].objectId || item.parentId !== newSequence[index].parentId)
        }
        return false;
    }

    const hasSuppressChange = (): boolean => {
        if (prevSuppress.length !== suppressedIds.length) {
            return true
        }
        if (prevSuppress.length > 0) {
            return prevSuppress.some((s: IPresentation) => !isSuppressed(suppressedIds, s))
        }
        return false
    }

    const hasNewSuppressChange = (): boolean => {
        if (suppressedIds.length > prevSuppress.length) {
            return true
        }
        if (suppressedIds.length > 0) {
            return suppressedIds.some((value: IPresentation) => !isSuppressed(prevSuppress, value))
        }
        return false
    }

    const resetState = () => {
        setOverId(null)
        setActiveId(null)
        setOffsetLeft(0)
    }

    const handleCollapse = (item: FlattenedItem<IInfoGoHeaderOrg>) => {
        if (item.children.length) {
            setOrgTree((orgTree: TreeItems<IInfoGoHeaderOrg>) =>
                setProperty(orgTree, item.id, 'collapsed', (value) => {
                    return !value
                })
            )
        }
    }

    const handleEditHeader = (item: FlattenedItem<IInfoGoHeaderOrg>) => {
        getHeader({ variables: { headerId: (-Number(item.id)).toString() }, fetchPolicy: 'network-only'})
    }

    const handleDeleteHeader = (item: FlattenedItem<IInfoGoHeaderOrg>) => {
        if (item.children.length > 0) {
            warn({
                title: "Warning!",
                content: 'Cannot delete Header that has child elements.'
            })
        } else {
            confirm({
                title: 'Remove Header?',
                content: 'The selected header will be removed. <br/>Are you sure you want to continue?',
                okText: <><DeleteIcon/> Remove</>,
                okBtnColor: 'danger'
            }).then(() => {
                deleteHeader({
                    variables: {
                        headerId: (-Number(item.id)).toString()
                    },
                    refetchQueries: [{
                        query: GET_INFOGO_HEADER_ORGS,
                        variables: { orgId: rootId }
                    }],
                    awaitRefetchQueries: true,
                    onCompleted: (data) => {
                        data && notifySuccess()
                    }
                });
            })
        }
    }

    const handleAddHeader = (item: FlattenedItem<IInfoGoHeaderOrg> | null) => {
        if (item) {
            setParentHeaderId((-Number(item?.id)).toString())
        }
        toggleAddHeader()
    }

    const isItemSuppressed = (item: FlattenedItem<IInfoGoHeaderOrg>) => {
        return suppressedIds.some((e: IPresentation) => e.typeName === item.typeName && e.objectId === item.presentationId)
    }

    const isSuppressed = (supIds: IPresentation[], item: IPresentation) => {
        return supIds.some((e: IPresentation) => e.typeName === item.typeName && e.objectId === item.objectId)
    }

    const onSuppressCheck = (event: any, item: FlattenedItem<IInfoGoHeaderOrg>) => {
        if (event.target.checked) {
            setSuppressedIds([...suppressedIds, {typeName: item.typeName!, objectId: item.presentationId!}])
        } else {
            setSuppressedIds(suppressedIds.filter(e => e.typeName !== item.typeName || e.objectId !== item.presentationId))
        }
        setTimeout(()=>onChange())
    }

    return (
        <div>
            {(loading || headerLoading || deleteHeaderLoading) && <SpinnerOverlay />}

            {orgTree.length === 0
                ?
                <div style={{ textAlign: 'center' }}>No headers or organizations found.</div>
                :
                <>
                  { !isFrenchTranslator && <div>
                        {org?.details?.category?.type === CategoryType.ORGANIZATION &&
                            <Button color='primary' disabled={isDirty() || !!org?.details?.shareLinkId} onClick={()=>handleAddHeader(null)} style={{ marginRight: '1rem' }}>
                                <AddIcon/> Add Header
                            </Button>
                        }
                    </div>}
                    <div className="infogo-grid-row grid-header">
                        <div className="infogo-header-col1">
                        </div>
                        <div className="infogo-header-col2">
                            Organization
                        </div>
                        <div className="infogo-header-col3">
                            Suppressed?
                        </div>
                    </div>
                    <div>
                        {!isFrenchTranslator && <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            measuring={measuring}
                            onDragStart={handleDragStart}
                            onDragMove={handleDragMove}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}>

                            <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
                                {flattenedItems.map((item : FlattenedItem<IInfoGoHeaderOrg>) => (
                                    <HeaderOrgItem
                                        key={item.id}
                                        id={item.id}
                                        shareCloned={!!org?.details?.shareLinkId}
                                        itemData={item}
                                        depth={item.id === activeId && projected ? projected.depth : item.depth}
                                        indentationWidth={INDENTATION_WIDTH}
                                        suppressed={isItemSuppressed(item)}
                                        onSuppressCheck={event => onSuppressCheck(event, item)}
                                        collapsed={Boolean(item.collapsed && item.children.length)}
                                        isDirty={isDirty()}
                                        onCollapse={item.children.length ? () => handleCollapse(item) : undefined}
                                        onEditHeader={() => handleEditHeader(item)}
                                        onDeleteHeader={() => handleDeleteHeader(item)}
                                        onAddHeader={() => handleAddHeader(item)}
                                    />
                                ))}
                                <DragOverlay
                                    dropAnimation={dropAnimationConfig}>
                                    {activeId && activeItem ? (
                                        <HeaderOrgItem
                                            id={activeId}
                                            shareCloned={!!org?.details?.shareLinkId}
                                            depth={activeItem.depth}
                                            clone
                                            childCount={getChildCount(orgTree, activeId) + 1}
                                            itemData={activeItem}
                                            suppressed={isItemSuppressed(activeItem)}
                                            onSuppressCheck={() => {}}
                                            indentationWidth={INDENTATION_WIDTH}
                                        />
                                    ) : null}
                                </DragOverlay>

                            </SortableContext>
                        </DndContext>}
                        { isFrenchTranslator && <div>
                            {flattenedItems.map((item : FlattenedItem<IInfoGoHeaderOrg>) => (
                                    <HeaderOrgItem
                                        key={item.id}
                                        id={item.id}
                                        shareCloned={!!org?.details?.shareLinkId}
                                        itemData={item}
                                        depth={item.id === activeId && projected ? projected.depth : item.depth}
                                        indentationWidth={INDENTATION_WIDTH}
                                        suppressed={isItemSuppressed(item)}
                                        onSuppressCheck={event => onSuppressCheck(event, item)}
                                        collapsed={Boolean(item.collapsed && item.children.length)}
                                        isDirty={isDirty()}
                                        onCollapse={item.children.length ? () => handleCollapse(item) : undefined}
                                        onEditHeader={() => handleEditHeader(item)}
                                        onDeleteHeader={() => handleDeleteHeader(item)}
                                        onAddHeader={() => handleAddHeader(item)}
                                    />
                                ))}
                        </div>

                        }
                    </div>
                </>
            }

            <HeaderAdd orgId={rootId} parentHeaderId={parentHeaderId} isOpen={addHeaderOpen} toggle={toggleAddHeader}></HeaderAdd>
            <HeaderUpdate orgId={rootId} header={header} isOpen={editHeaderOpen} toggle={toggleEditHeader}></HeaderUpdate>
        </div>
    )
})

export default HeaderOrgs
